/**
 * notificationTypes.js — icon, screen-reader label and visible text for each
 * in-app deal notification (`GET /api/offer-notifications`).
 *
 * A row stores no prose: just its `notification_type`, a `copy_key` with
 * non-prose `copy_params` (the reminder `tier`) and the viewer's
 * `viewer_role` on the offer. The WI texts and labels are legal's copy §6.1
 * (docs/legal/states/WI/contingency-notices-copy.v1.md, `WI.notification.*`,
 * mirrored word for word in ContingencyHub/contract/contractCopy.js):
 *
 * - exact types: `WI.notification.<type>.label` / `.text`;
 * - reminders `wi_<kind>_deadline_<tier>`: `.text.buyer` / `.text.seller` by
 *   the viewer's role, `.text` when the role isn't known, `.text.no_when`
 *   when the tier has no `WI.notification.when.<tier>`. A reminder can be read
 *   days after it was sent, so the drawer shows its sent date next to it.
 *
 * Non-WI Contingency Hub types (deadline reminders, `contingency_expired`,
 * repair, appraisal, financing, `contingency_removed`,
 * `all_contingencies_cleared`) have no WI key. They use legal's state-neutral
 * `GEN.notification.<type>.label` / `.text`
 * (docs/legal/general/notification-and-advisor-copy.v1.md Part 2, mirrored in
 * genericNotificationCopy.js), and `.text.<event>` when `copy_params.event`
 * names one. The WI mapping is always tried first, so a GEN text never
 * overrides a WI one. These rows also carry `copy_params.contingency_id` and
 * `contingency_type`, which name the contingency on its own line.
 *
 * A type this file doesn't recognize keeps the generic "Notification" label;
 * never guess a legal meaning for it.
 *
 * Naming the contingency: the clock and the notice service write one row per
 * contingency, with `copy_params.catalog_key` and `copy_params.contingency_id`.
 * Several rows can carry the same §6.1 text (e.g. two deadlines passing in
 * one run), so the drawer shows the contingency's catalog name as its own
 * line (`contingencySubject`) and never splices it into legal's text.
 */

import { CONTRACT_COPY, contractCopy } from '../../ContingencyHub/contract/contractCopy';
import { getContingencyLabel } from '../contingencyCatalog';
import { NAV_GROUPS } from '../../../navigation/navConfig';
import { genCopy } from './genericNotificationCopy';
import { copyText } from '../../SellerDashboard/paperworkCopy';

const COPY_PREFIX = 'WI.notification.';
const GEN_PREFIX = 'GEN.notification.';

// Offer lifecycle types (`offer_<status>`, or the bare status). The bare status
// reaches this map directly (the prefixed `offer_<status>` is resolved to the
// same copy upstream), so each carries the generic body text, not just a label
// — otherwise the drawer would show the screen-reader label ("Accepted
// notification") as its body.
export const OFFER_TYPE_META = {
  accepted:  { icon: '✅', label: 'Accepted notification',  text: genCopy('GEN.notification.offer_accepted.text') },
  countered: { icon: '🔄', label: 'Countered notification', text: genCopy('GEN.notification.offer_countered.text') },
  rejected:  { icon: '❌', label: 'Rejected notification',  text: genCopy('GEN.notification.offer_rejected.text') },
  withdrawn: { icon: '🚫', label: 'Withdrawn notification', text: genCopy('GEN.notification.offer_withdrawn.text') },
  submitted: { icon: '📝', label: 'Submitted notification', text: genCopy('GEN.notification.offer_submitted.text') },
  expired:   { icon: '⏰', label: 'Expired notification',   text: genCopy('GEN.notification.offer_expired.text') },
};

/** Copy §6.1 exact types → icon. Neutral icons: an ended offer is never celebrated. */
export const CONTINGENCY_TYPE_ICONS = {
  contingency_notice_received: '📨',
  contingency_flagged: '🚩',
  contingency_treated_as_satisfied: '📋',
  contingency_cure_lapsed: '📋',
  contingency_land_division_lapsed: '📋',
  contingency_deal_ended: '📁',
  listing_relist_prompt: '🏠',
  // Admin release of a held contingency (copy §14.2).
  contingency_admin_updated: '📋',
};

/** Copy §6.1 reminder bases (backend `contingency_expiry.WI_REMINDER_PREFIX_*`). */
export const REMINDER_BASES = ['wi_contingency_deadline', 'wi_cure_deadline', 'wi_land_division_deadline'];

export const ROLES = ['buyer', 'seller'];

export const DEFAULT_META = { icon: '🔔', label: 'Notification' };

/**
 * Non-WI exact types with `GEN.notification.<type>` copy → icon (legal §2.3:
 * neutral, nothing celebratory, especially for the all-clear).
 */
export const GEN_TYPE_ICONS = {
  contingency_expired: '📋',
  contingency_removed: '📋',
  all_contingencies_cleared: '📋',
  repair_requested: '🔧',
  repair_responded: '🔧',
  appraisal_gap: '📄',
  financing_at_risk: '📄',
  // The seller deleted the listing (WI copy file §15.3, state-neutral): a
  // buyer's pending offer was closed, or a requested tour was cancelled.
  offer_closed_listing_deleted: '📄',
  tour_cancelled_listing_deleted: '📅',
  // Part 3 (2026-10-16): offer lifecycle. Neutral icons: never celebrate.
  offer_submitted: '📝',
  offer_countered: '🔄',
  offer_accepted: '📄',
  offer_rejected: '📁',
  offer_withdrawn: '📁',
  offer_expired: '📁',
  // The platform recorded the deal closed (legal 2026-10-19: neutral, never celebrate).
  transaction_closed: '📁',
};

/** WI condo package rows: the server sets `WI.notification.condo_documents_*`. */
const CONDO_KEY_PREFIX = `${COPY_PREFIX}condo_documents_`;

/** Non-WI reminders: `contingency_deadline_<tier>` (backend `contingency_expiry._tier`). */
export const GEN_DEADLINE_BASE = 'contingency_deadline';
export const GEN_DEADLINE_TIERS = ['today', '1d', '3d', '5d'];

/**
 * The copy §6.1 base (`contingency_deal_ended`, `wi_cure_deadline`, ...) and
 * reminder tier for a notification, from its `copy_key` / `copy_params` when
 * the API sent them, else from its type.
 *
 * @param {string|null|undefined} type
 * @param {{copy_key?: string, copy_params?: object}} [notif]
 * @returns {{base: string, tier: (string|null), reminder: boolean}|null}
 */
export function resolveCopyBase(type, notif = {}) {
  const fromKey = typeof notif.copy_key === 'string' && notif.copy_key.startsWith(COPY_PREFIX)
    ? notif.copy_key.slice(COPY_PREFIX.length)
    : null;
  const paramTier = notif.copy_params && typeof notif.copy_params.tier === 'string'
    ? notif.copy_params.tier
    : null;
  const candidates = [fromKey, type].filter(Boolean);
  for (const candidate of candidates) {
    if (CONTINGENCY_TYPE_ICONS[candidate]) return { base: candidate, tier: null, reminder: false };
    if (REMINDER_BASES.includes(candidate)) return { base: candidate, tier: paramTier, reminder: true };
    const base = REMINDER_BASES.find((b) => candidate.startsWith(`${b}_`));
    if (base) return { base, tier: paramTier || candidate.slice(base.length + 1), reminder: true };
  }
  return null;
}

/**
 * The non-WI type a notification's `GEN.notification.*` copy is keyed by, from
 * its `copy_key` when it is a GEN key, else from its type. Callers try
 * {@link resolveCopyBase} first: a WI key always wins.
 *
 * @param {string|null|undefined} type
 * @param {{copy_key?: string, copy_params?: object}} [notif]
 * @returns {{base: string, tier: (string|null), reminder: boolean}|null}
 */
export function resolveGenericCopy(type, notif = {}) {
  const fromKey = typeof notif.copy_key === 'string' && notif.copy_key.startsWith(GEN_PREFIX)
    ? notif.copy_key.slice(GEN_PREFIX.length)
    : null;
  const paramTier = notif.copy_params && typeof notif.copy_params.tier === 'string'
    ? notif.copy_params.tier
    : null;
  const candidates = [fromKey, type].filter(Boolean);
  for (const candidate of candidates) {
    if (GEN_TYPE_ICONS[candidate]) return { base: candidate, tier: null, reminder: false };
    if (candidate === GEN_DEADLINE_BASE) return { base: GEN_DEADLINE_BASE, tier: paramTier, reminder: true };
    if (candidate.startsWith(`${GEN_DEADLINE_BASE}_`)) {
      return { base: GEN_DEADLINE_BASE, tier: paramTier || candidate.slice(GEN_DEADLINE_BASE.length + 1), reminder: true };
    }
  }
  return null;
}

/**
 * Icon, label and text from legal's `GEN.notification.*` copy.
 *
 * Reminders use `contingency_deadline_<tier>`, or `.text.no_when` for a tier
 * with no key. Other types use `.text.<event>` when `copy_params.event` has a
 * key (e.g. `contingency_removed.text.exercised`), else the generic `.text`.
 * Facts in the params (an appraisal gap, a proposed date) are never spliced in.
 *
 * Unlike WI reminders this doesn't set `reminder`: the drawer's "Sent <date>"
 * line is formatted in Wisconsin's time zone, which a non-WI deal mustn't get.
 * The relative time is still shown.
 */
function genericMeta(resolved, params) {
  if (resolved.reminder) {
    const known = GEN_DEADLINE_TIERS.includes(resolved.tier);
    const key = `${GEN_PREFIX}${GEN_DEADLINE_BASE}_${known ? resolved.tier : 'today'}`;
    return {
      icon: '⏳',
      label: genCopy(`${key}.label`),
      text: known ? genCopy(`${key}.text`) : genCopy(`${GEN_PREFIX}${GEN_DEADLINE_BASE}.text.no_when`),
    };
  }
  const prefix = `${GEN_PREFIX}${resolved.base}`;
  const event = params && typeof params.event === 'string' ? params.event : null;
  return {
    icon: GEN_TYPE_ICONS[resolved.base],
    label: genCopy(`${prefix}.label`),
    text: (event && genCopy(`${prefix}.text.${event}`)) || genCopy(`${prefix}.text`),
  };
}

/**
 * The `.text.<outcome>` variant of a WI row when `copy_params.outcome` names
 * one legal wrote (copy §15.1:
 * `contingency_admin_updated.text.record_not_applicable`), else null. The
 * label never changes.
 *
 * @param {string} prefix - `WI.notification.<type>`
 * @param {object} [params] - The row's `copy_params`.
 * @returns {string|null}
 */
export function outcomeTextKey(prefix, params) {
  const outcome = params && typeof params.outcome === 'string' ? params.outcome : null;
  if (!outcome || !/^[a-z_]+$/.test(outcome)) return null;
  const key = `${prefix}.text.${outcome}`;
  return CONTRACT_COPY[key] ? key : null;
}

/** Copy §6.1 reminder text for a role and tier. */
export function reminderText(base, role, tier) {
  const prefix = `${COPY_PREFIX}${base}`;
  const when = tier ? contractCopy(`${COPY_PREFIX}when.${tier}`) : null;
  if (!when) return contractCopy(`${prefix}.text.no_when`);
  const roleKey = ROLES.includes(role) ? `${prefix}.text.${role}` : null;
  const key = roleKey && CONTRACT_COPY[roleKey] ? roleKey : `${prefix}.text`;
  return contractCopy(key, { when });
}

/**
 * Icon, screen-reader label and visible text for a notification.
 *
 * @param {string|null|undefined} type The row's `notification_type`.
 * @param {{copy_key?: string, copy_params?: object, viewer_role?: string}} [notif]
 * @returns {{icon: string, label: string, text?: string, reminder?: boolean}}
 */
export function getNotificationMeta(type, notif = {}) {
  if (!type && !notif.copy_key) return DEFAULT_META;
  const resolved = resolveCopyBase(type, notif);
  if (resolved) {
    const prefix = `${COPY_PREFIX}${resolved.base}`;
    if (resolved.reminder) {
      return {
        icon: '⏳',
        label: contractCopy(`${prefix}.label`),
        text: reminderText(resolved.base, notif.viewer_role, resolved.tier),
        reminder: true,
      };
    }
    const variantKey = outcomeTextKey(prefix, notif.copy_params);
    return {
      icon: CONTINGENCY_TYPE_ICONS[resolved.base],
      label: contractCopy(`${prefix}.label`),
      text: (variantKey && contractCopy(variantKey)) || contractCopy(`${prefix}.text`),
    };
  }
  // Condo package rows (paperwork copy §10), only when the server set the WI
  // key: another state's condo rule never gets Wisconsin citations.
  if (typeof notif.copy_key === 'string' && notif.copy_key.startsWith(CONDO_KEY_PREFIX)) {
    const kind = notif.copy_params && typeof notif.copy_params.kind === 'string' ? notif.copy_params.kind : null;
    return {
      icon: '📄',
      label: copyText(`${notif.copy_key}.label`),
      text: (kind && copyText(`${notif.copy_key}.text.${kind}`)) || copyText(`${notif.copy_key}.text`),
    };
  }
  const generic = resolveGenericCopy(type, notif);
  if (generic) return genericMeta(generic, notif.copy_params);
  if (type) {
    const status = type.startsWith('offer_') ? type.slice('offer_'.length) : type;
    if (OFFER_TYPE_META[status]) return OFFER_TYPE_META[status];
  }
  return DEFAULT_META;
}

/**
 * The contingency a deal notification is about, or null. WI rows are named
 * from `copy_params.catalog_key` (the offer catalog's label); non-WI hub rows
 * from `catalog_key`, else their `contingency_type`.
 *
 * @param {{notification_type?: string, copy_key?: string, copy_params?: object}} notif
 * @returns {string|null}
 */
export function contingencySubject(notif) {
  // The signed offer's own heading (e.g. "Unit inspection" on a condo), when
  // the API knows it, over the catalog's generic label (QA 2026-10-16).
  if (notif && typeof notif.contingency_heading === 'string' && notif.contingency_heading.trim()) {
    return notif.contingency_heading.trim();
  }
  const params = notif && notif.copy_params;
  if (!params || typeof params !== 'object') return null;
  const wi = resolveCopyBase(notif.notification_type, notif);
  // Offer lifecycle rows have GEN copy (Part 3) but are never about a contingency.
  const isOfferRow = /^offer_/.test(notif.notification_type || '');
  const generic = !wi && !isOfferRow && resolveGenericCopy(notif.notification_type, notif);
  if (!wi && !generic) return null;
  let key = typeof params.catalog_key === 'string' ? params.catalog_key.trim() : '';
  if (!key && generic && typeof params.contingency_type === 'string') key = params.contingency_type.trim();
  if (!key) return null;
  return getContingencyLabel(key) || null;
}

/**
 * The contingency row a deal notification is about (`copy_params.contingency_id`), or null.
 *
 * @param {{copy_params?: object}} notif
 * @returns {number|null}
 */
export function notificationContingencyId(notif) {
  const id = notif && notif.copy_params ? Number(notif.copy_params.contingency_id) : NaN;
  return Number.isInteger(id) && id > 0 ? id : null;
}

/**
 * The transaction a deal notification is about (`copy_params.transaction_id`), or null.
 *
 * @param {{copy_params?: object}} notif
 * @returns {number|null}
 */
export function notificationTransactionId(notif) {
  const id = notif && notif.copy_params ? Number(notif.copy_params.transaction_id) : NaN;
  return Number.isInteger(id) && id > 0 ? id : null;
}

/** Bases with a `base:buyer` / `base:seller` tab (e.g. `contingencies`, `offers`). */
const ROLE_TAB_BASES = new Set(
  NAV_GROUPS.flatMap((group) => group.items)
    .map((item) => item.key)
    .filter((key) => key.includes(':'))
    .map((key) => key.slice(0, key.indexOf(':'))),
);

/**
 * Where a notification opens: its `link`, scoped to the viewer's side of the
 * deal (`contingencies:seller`) when the tab has a buyer and a seller view,
 * plus the transaction and contingency it is about. A user who is the buyer on
 * one deal and the seller on another lands on the right hub either way.
 *
 * @param {{link?: string, viewer_role?: string, offer_id?: number, copy_params?: object}} notif
 * @returns {{tab: string, params: {offerId?: number, transactionId?: number, contingencyId?: number}}|null}
 */
export function notificationDestination(notif) {
  const link = notif && typeof notif.link === 'string' ? notif.link.trim() : '';
  if (!link) return null;
  const scoped = !link.includes(':') && ROLE_TAB_BASES.has(link) && ROLES.includes(notif.viewer_role);
  const tab = scoped ? `${link}:${notif.viewer_role}` : link;
  const params = {};
  if (notif.offer_id != null) params.offerId = notif.offer_id;
  const transactionId = notificationTransactionId(notif);
  if (transactionId) params.transactionId = transactionId;
  const contingencyId = notificationContingencyId(notif);
  if (contingencyId) params.contingencyId = contingencyId;
  return { tab, params };
}

/** What the drawer shows: legal's text for the type, else the type's label. */
export function notificationText(notif) {
  const meta = getNotificationMeta(notif?.notification_type, notif || {});
  return meta.text || meta.label;
}
